<template>
  <div class="client-inputs-tab">
    <!-- Sub-tabs for Client Inputs -->
    <div class="card-header py-3 px-0">
      <div class="card-title flex-column" style="justify-content: space-evenly">
        <h3 class="card-label font-weight-bolder text-dark m-0">
          Client Inputs
        </h3>
      </div>
    </div>

    <!-- Sub-tabs Navigation -->
    <div class="sub-tabs-container">
      <v-tabs v-model="activeTab" class="sub-tabs">
        <v-tab>Squad Ratings</v-tab>
        <v-tab>Talents Ratings</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Squad Ratings Tab Content -->
        <v-tab-item>
          <div class="tab-content">
            <div class="table-responsive mb-0">
              <table
                class="table table-head-custom table-vertical-center table-head-bg table-borderless"
              >
                <thead>
                  <tr class="text-left">
                    <th>Rating Submission Date</th>
                    <th>Rated Month</th>
                    <th>Rating Score</th>
                    <th>Rating By</th>
                    <th>Tags Selected</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="squadLoading">
                    <td colspan="5" class="text-center text-muted">
                      Loading...
                    </td>
                  </tr>
                  <tr
                    v-if="!squadLoading && paginatedSquadRatings.length === 0"
                  >
                    <td colspan="5" class="text-center text-muted">
                      No data available
                    </td>
                  </tr>
                  <tr v-for="item in paginatedSquadRatings" :key="item.id">
                    <td>{{ formatDate(item.ratingDate) }}</td>
                    <td>{{ formatMonth(item.ratedMonth) || "—" }}</td>
                    <td>
                      <v-rating
                        v-model="item.rating"
                        readonly
                        dense
                        small
                        color="primary"
                        length="5"
                        empty-icon="mdi-star"
                        full-icon="mdi-star"
                        background-color="#E0E0E0"
                      ></v-rating>
                    </td>
                    <td>{{ item.ratingBy || "—" }}</td>
                    <td>
                      {{
                        item.criteria && item.criteria.length > 0
                          ? item.criteria.join(", ")
                          : "—"
                      }}
                    </td>
                    <td>
                      <v-btn
                        v-if="item.rating > 0"
                        depressed
                        color="primary"
                        class="mr-2"
                        @click="viewReview(item)"
                      >
                        View Review
                      </v-btn>
                      <span v-else class="text-muted">No review</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Squad Ratings Pagination -->
            <div class="pagination-container">
              <p
                v-if="squadRatings.length > 0"
                class="pagination-container__desc"
              >
                {{
                  $t("showing_results", {
                    length: paginatedSquadRatings.length,
                    total: squadRatings.length,
                  })
                }}
              </p>
              <b-pagination
                v-if="squadTotalPages > 1"
                v-model="squadPage"
                :total-rows="squadRatings.length"
                :per-page="squadPerPage"
                align="center"
                last-number
                first-number
              ></b-pagination>
            </div>
          </div>
        </v-tab-item>

        <!-- Talents Ratings Tab Content -->
        <v-tab-item>
          <div class="tab-content">
            <!-- Search Filter -->
            <div class="search-filter-container mt-4 mb-0">
              <v-text-field
                v-model="talentSearchQuery"
                label="Search by talent name"
                outlined
                dense
                prepend-inner-icon="mdi-magnify"
                class="search-input"
              ></v-text-field>
            </div>
            <div class="table-responsive mb-0">
              <table
                class="table table-head-custom table-vertical-center table-head-bg table-borderless"
              >
                <thead>
                  <tr class="text-left">
                    <th>Name</th>
                    <th>Role</th>
                    <th>Rating Submission Date</th>
                    <th>Rated Month</th>
                    <th>Rating Score</th>
                    <th>Rating By</th>
                    <th>Tags Selected</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="talentLoading">
                    <td colspan="8" class="text-center text-muted">
                      Loading...
                    </td>
                  </tr>
                  <tr
                    v-if="!talentLoading && paginatedTalentRatings.length === 0"
                  >
                    <td colspan="8" class="text-center text-muted">
                      No data available
                    </td>
                  </tr>
                  <tr v-for="item in paginatedTalentRatings" :key="item.id">
                    <td>
                      <router-link
                        v-if="item.talentId"
                        :to="`/dashboard/talent/${item.talentId}`"
                        target="_blank"
                      >
                        {{ item.name || "—" }}
                      </router-link>
                      <span v-else>{{ item.name || "—" }}</span>
                    </td>
                    <td>{{ item.role || "—" }}</td>
                    <td>{{ formatDate(item.ratingDate) }}</td>
                    <td>{{ formatMonth(item.ratedMonth) || "—" }}</td>
                    <td>
                      <v-rating
                        v-model="item.rating"
                        readonly
                        dense
                        small
                        color="primary"
                        length="5"
                        empty-icon="mdi-star"
                        full-icon="mdi-star"
                        background-color="#E0E0E0"
                      ></v-rating>
                    </td>
                    <td>{{ item.ratingBy || "—" }}</td>
                    <td>
                      {{
                        item.criteria && item.criteria.length > 0
                          ? item.criteria.join(", ")
                          : "—"
                      }}
                    </td>
                    <td>
                      <v-btn
                        v-if="item.rating > 0"
                        depressed
                        color="primary"
                        class="mr-2"
                        @click="viewReview(item)"
                      >
                        View Review
                      </v-btn>
                      <span v-else class="text-muted">No review</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Talents Ratings Pagination -->
            <div class="pagination-container">
              <p
                v-if="talentRatings.length > 0"
                class="pagination-container__desc"
              >
                {{
                  $t("showing_results", {
                    length: paginatedTalentRatings.length,
                    total: talentRatings.length,
                  })
                }}
              </p>
              <b-pagination
                v-if="talentTotalPages > 1"
                v-model="talentPage"
                :total-rows="talentRatings.length"
                :per-page="talentPerPage"
                align="center"
                last-number
                first-number
              ></b-pagination>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Review Modal -->
    <v-dialog v-model="reviewDialog" max-width="600px">
      <v-card>
        <v-card-title>Review Details</v-card-title>
        <v-card-text>
          <div v-if="selectedReview">
            <div class="mb-4">
              <strong>Rating:</strong>
              <star-rating-with-labels
                :value="selectedReview.rating"
                :disable-hover="true"
                :interactive="false"
                :star-size="48"
                :labels="['worst', 'Bad', 'Neutral', 'Good', 'Excellent']"
                :active-color="'#FFC107'"
                :inactive-color="'#E0E0E0'"
                class="ma-2"
              />
            </div>
            <div
              class="mb-4"
              v-if="
                selectedReview.criteria && selectedReview.criteria.length > 0
              "
            >
              <strong>Tags:<br /></strong>
              <v-chip
                v-for="tag in selectedReview.criteria"
                :key="tag"
                color="primary"
                class="ma-1"
                small
                outlined
                >{{ tag }}</v-chip
              >
            </div>
            <div v-else class="mb-4">
              <strong>Tags:<br /></strong>
              <p class="text-muted">No tags</p>
            </div>
            <div v-if="selectedReview.review">
              <strong>Review:</strong>
              <p>{{ selectedReview.review }}</p>
            </div>
            <div v-else class="mb-4">
              <strong>Review:<br /></strong>
              <p class="text-muted">No review</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reviewDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import StarRatingWithLabels from "@/components/shared/StarRatingWithLabels.vue";
import { format, parseISO, isValid } from "date-fns";
import debounce from "lodash/debounce";

export default {
  name: "ClientInputsTab",
  components: {
    StarRatingWithLabels,
  },
  props: {
    selectedProject: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      activeTab: 0, // Default to Squad Ratings tab (index 0)
      reviewDialog: false,
      selectedReview: null,
      // Pagination for Talent Ratings
      talentPage: 1,
      talentPerPage: 8,
      talentRatings: [], // <-- new array for fetched ratings
      talentLoading: false, // loading state for talent ratings
      talentSearchQuery: "", // Search query for talent name
      // Pagination for Squad Ratings
      squadPage: 1,
      squadPerPage: 8,
      squadRatings: [], // <-- new array for fetched squad ratings
      squadLoading: false, // loading state for squad ratings
    };
  },
  computed: {
    ...mapGetters({}),
    // Talent Ratings Pagination
    talentTotalPages() {
      return Math.ceil(this.talentRatings.length / this.talentPerPage);
    },
    talentStartIndex() {
      return (this.talentPage - 1) * this.talentPerPage;
    },
    talentEndIndex() {
      return Math.min(
        this.talentStartIndex + this.talentPerPage,
        this.talentRatings.length
      );
    },
    paginatedTalentRatings() {
      return this.talentRatings.slice(
        this.talentStartIndex,
        this.talentEndIndex
      );
    },
    // Squad Ratings Pagination
    squadTotalPages() {
      return Math.ceil(this.squadRatings.length / this.squadPerPage);
    },
    squadStartIndex() {
      return (this.squadPage - 1) * this.squadPerPage;
    },
    squadEndIndex() {
      return Math.min(
        this.squadStartIndex + this.squadPerPage,
        this.squadRatings.length
      );
    },
    paginatedSquadRatings() {
      return this.squadRatings.slice(this.squadStartIndex, this.squadEndIndex);
    },
  },
  watch: {
    selectedProject: {
      handler(newProject, oldProject) {
        // Only refetch if project actually changed and we have a valid project
        if (newProject && newProject !== oldProject) {
          this.fetchData();
        }
      },
      immediate: false,
    },
    talentSearchQuery(newValue) {
      this.talentPage = 1;
      const trimmedValue = newValue ? newValue.trim() : "";
      if ((trimmedValue && trimmedValue.length > 1) || newValue === "") {
        this.debouncedSearchTalentRatings();
      }
    },
  },
  methods: {
    ...mapActions({
      getClientRatings: "clients/getClientRatings",
    }),
    formatMonth(month) {
      if (!month) return "—";
      return format(new Date(month), "MMMM yyyy");
    },
    formatDate(date) {
      if (!date) return "—";
      let d;
      try {
        d = typeof date === "string" ? parseISO(date) : new Date(date);
      } catch {
        return "—";
      }
      if (!isValid(d)) return "—";
      return format(d, "yyyy-MM-dd");
    },
    viewReview(item) {
      this.selectedReview = item;
      this.reviewDialog = true;
    },
    async fetchTalentRatings() {
      this.talentLoading = true;
      this.talentRatings = [];

      const params = {
        perPage: 10,
        type: "client_rate_talent",
        project_id: this.selectedProject || this.$route.params.project_id,
      };

      if (this.talentSearchQuery && this.talentSearchQuery.trim()) {
        params.talent_name = this.talentSearchQuery.trim();
      }

      this.getClientRatings(params)
        .then((res) => {
          this.talentRatings = (res.data || []).map((item) => ({
            id: item.id,
            talentId: item.ratable?.id,
            name: item.ratable?.talent_name,
            role: item.ratable?.role_name,
            ratingDate: item.rated_at,
            rating: item.score,
            ratingBy: item.rated_by?.name,
            criteria: [
              ...(item.rating_options?.positive?.map((opt) => opt.label) || []),
              ...(item.rating_options?.negative?.map((opt) => opt.label) || []),
            ],
            review: item.review,
            ratedMonth: item.created_at,
          }));
        })
        .catch(() => {
          this.talentRatings = [];
        })
        .finally(() => {
          this.talentLoading = false;
        });
    },
    async fetchData() {
      this.squadLoading = true;

      this.fetchTalentRatings();

      this.getClientRatings({
        perPage: 10,
        type: "client_rate_project",
        project_id: this.selectedProject || this.$route.params.project_id,
      })
        .then((res) => {
          this.squadRatings = (res.data || []).map((item) => ({
            id: item.id,
            squadName:
              item.ratable?.project_name || item.client_project?.project_name,
            ratingDate: item.rated_at,
            rating: item.score,
            ratingBy: item.rated_by?.name,
            criteria: [
              ...(item.rating_options?.positive?.map((opt) => opt.label) || []),
              ...(item.rating_options?.negative?.map((opt) => opt.label) || []),
            ],
            review: item.review,
            ratedMonth: item.created_at,
          }));
        })
        .catch(() => {
          this.squadRatings = [];
        })
        .finally(() => {
          this.squadLoading = false;
        });
    },
  },
  mounted() {
    this.debouncedSearchTalentRatings = debounce(() => {
      this.fetchTalentRatings();
    }, 500);

    this.fetchData();
  },
};
</script>

<style scoped>
.client-inputs-tab {
  padding: 1rem;
}

.table-responsive {
  margin-bottom: 0;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #181c32;
  border-collapse: collapse;
}

.table th {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.1rem;
  padding: 0.75rem 1rem;
  background-color: #f3f6f9;
  border-bottom: 1px solid #ebedf3;
}

.table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #ebedf3;
}

.table-borderless td,
.table-borderless th {
  border: 0;
}

.table-head-bg th {
  background-color: #f3f6f9;
}

.table-vertical-center td {
  vertical-align: middle;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: wrap;
  min-height: 70px;
  padding: 0 2.25rem;
  background-color: transparent;
  border-bottom: 1px solid #ebedf3;
}

.card-title {
  display: flex;
  margin: 0.5rem;
  margin-left: 0;
  flex-wrap: wrap;
  align-items: center;
}

.card-label {
  display: flex;
  align-items: center;
  margin: 0;
  margin-left: 0.75rem;
  margin-right: 0.5rem;
  font-weight: 500;
  color: #181c32;
  font-size: 1.275rem;
}

.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
}

.pagination-container__desc {
  margin-bottom: 0.5rem;
  color: #7e8299;
  font-size: 0.9rem;
}

.star-rating-with-labels {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.star-labels {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 180px;
  margin-top: 0.2rem;
}
.star-labels span {
  flex: 1;
  text-align: center;
  color: #3d2586;
  font-weight: bold;
  font-size: 0.85rem;
  text-transform: capitalize;
}

/* Sub-tabs styling */
.sub-tabs-container {
  margin-bottom: 1rem;
  border-bottom: 1px solid #ebedf3;
}

.sub-tabs {
  background-color: transparent;
}

.tab-content {
  padding: 1rem 0;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.search-filter-container {
  max-width: 400px;
}

.search-input {
  margin-bottom: 0;
}
</style>
